import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SignOut, MagnifyingGlass, ArrowLeft, House, Queue, CalendarCheck, CalendarBlank, Users, FileText, User, CaretDown, List, ChartBar, Syringe, CurrencyCircleDollar, Bell, Ticket, X as XIcon } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { AuthModal } from "@/components/AuthForms";
import { useBranding } from "@/hooks/useBranding";
import { InlineLoader } from "@/components/LoadingSkeleton";

export default function Navbar({ platformName }) {
  const branding = useBranding();
  const name = platformName || branding.product_name;
  const tagline = branding.tagline || "Find care near you";
  const logoUrl = branding.logo_url || "/favicon.png";
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [procBadge, setProcBadge] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const profileRef = useRef(null);
  const notifRef = useRef(null);
  const intervalRef = useRef(null);
  const notifIntervalRef = useRef(null);

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
    setNotifOpen(false);
  }, [location.pathname]);

  const onLogout = async () => {
    setConfirmOpen(false);
    await logout();
    navigate("/", { replace: true });
  };

  const showBackButton = location.pathname !== "/";

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) navigate(`/doctors?q=${encodeURIComponent(q)}`);
    else navigate("/doctors");
  };

  const showSearch = location.pathname === "/";

  const openAuth = (mode) => {
    setAuthMode(mode);
    setAuthModalOpen(true);
    setMobileOpen(false);
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (user?.role !== "doctor") return;
    const fetchBadge = async () => {
      try {
        const { data } = await api.get("/procedures/requests/pending-count");
        setProcBadge(data.count || 0);
      } catch { /* ignore */ }
    };
    fetchBadge();
    intervalRef.current = setInterval(fetchBadge, 30000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [user]);

  // Fetch unread notification count
  useEffect(() => {
    if (!user || user.role === "admin") return;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/notifications/inbox/unread-count");
        setUnreadCount(data.count || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    notifIntervalRef.current = setInterval(fetchUnread, 30000);
    return () => { if (notifIntervalRef.current) clearInterval(notifIntervalRef.current); };
  }, [user]);

  const openNotifPanel = async () => {
    if (notifOpen) { setNotifOpen(false); return; }
    setNotifOpen(true);
    setNotifLoading(true);
    try {
      const { data } = await api.get("/notifications/inbox?limit=5");
      setNotifications(data.notifications || []);
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  };

  const markNotifRead = async (id) => {
    try {
      await api.patch("/notifications/inbox/read", { notification_id: id });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await api.patch("/notifications/inbox/read", { all: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const isPatient = user?.role === "patient";
  const isDoctor = user?.role === "doctor";
  const isAdmin = user?.role === "admin";

  const activeStyle = (path) =>
    location.pathname === path
      ? "bg-primary text-primary-foreground"
      : "text-muted-foreground hover:text-foreground hover:bg-muted";

  const mobileLinkClass = (path) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
      location.pathname === path ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
    }`;

  const PATIENT_LINKS = [
    { icon: Queue, label: "Live Queue", path: "/my-queue" },
    { icon: CalendarCheck, label: "My Bookings", path: "/dashboard" },
    { icon: Users, label: "Family", path: "/family" },
    { icon: Syringe, label: "Treatments", path: "/my-treatments" },
    { icon: FileText, label: "Medical Records", path: "/medical-records" },
  ];

  const DOCTOR_LINKS = [
    { icon: Queue, label: "Today's Queue", path: "/doctor/queue" },
    { icon: ChartBar, label: "Dashboard", path: "/doctor" },
    { icon: Syringe, label: "Treatment Plans", path: "/doctor/treatment-plans" },
  ];

  const APPOINTMENT_SUBLINKS = [
    { label: "All appointments", path: "/doctor/appointments" },
    { label: "Future appointments", path: "/doctor/appointments?filter=future" },
  ];

  const [appointmentsOpen, setAppointmentsOpen] = useState(false);

  const NotifBell = () => (
    <div className="relative" ref={notifRef}>
      <button
        type="button"
        onClick={openNotifPanel}
        className="relative p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} weight="duotone" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[10px] font-bold min-w-[16px] h-[16px] flex items-center justify-center rounded-full px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
      {notifOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-2xl border border-border bg-card shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold text-foreground">Notifications</span>
            {unreadCount > 0 && (
              <button type="button" onClick={markAllRead} className="text-xs text-primary font-semibold hover:underline">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifLoading ? (
              <InlineLoader message="Loading notifications" className="m-3" />
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-muted-foreground">No notifications yet</div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => { if (!n.read) markNotifRead(n.id); }}
                  className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-muted/50 transition-colors flex items-start gap-3 ${n.read ? "" : "bg-primary/[0.03]"}`}
                >
                  {!n.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />}
                  {n.read && <span className="w-2 h-2 shrink-0 mt-1.5" />}
                  <div className="min-w-0 flex-1">
                    <div className={`text-xs ${n.read ? "text-foreground" : "text-foreground font-semibold"}`}>{n.title}</div>
                    <div className="text-[11px] text-muted-foreground truncate mt-0.5">{n.body}</div>
                    <div className="text-[10px] text-muted-foreground/60 mt-0.5">{timeAgo(n.created_at)}</div>
                  </div>
                </button>
              ))
            )}
          </div>
          <Link to="/notifications" onClick={() => setNotifOpen(false)} className="block px-4 py-3 text-center text-xs font-semibold text-primary border-t border-border hover:bg-muted/50 transition-colors">
            View all notifications
          </Link>
        </div>
      )}
    </div>
  );

  return (
    <>
    <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-40">
      <div className="workspace-page py-3 flex items-center justify-between gap-2">
        {/* Left side */}
        <div className="flex items-center gap-1 sm:gap-3 min-w-0">
          <button
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="sm:hidden p-2 rounded-full hover:bg-muted transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <XIcon size={20} weight="bold" /> : <List size={20} weight="bold" />}
          </button>
          {showBackButton && !isPatient && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={goBack}
              aria-label="Go back"
              className="rounded-full shrink-0"
              data-testid="nav-back-btn"
            >
              <ArrowLeft size={18} weight="bold" />
            </Button>
          )}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
            <img src={logoUrl} alt={name} className="w-7 h-7 sm:w-8 sm:h-8 rounded object-cover" />
            <div className="min-w-0 leading-none">
              <div className="font-display font-bold text-foreground tracking-tight text-sm sm:text-base truncate">{name}</div>
              <div className="text-[10px] sm:text-[11px] text-muted-foreground font-medium truncate mt-0.5">{tagline}</div>
            </div>
          </Link>
        </div>

        {/* Desktop nav links */}
        <nav className="hidden sm:flex items-center gap-1 sm:gap-1.5">
          {isPatient ? (
            <>
              <Link to="/" className={`inline-flex items-center gap-1.5 text-sm font-semibold p-2 rounded-full transition-colors ${activeStyle("/")}`} title="Home">
                <House size={18} weight="duotone" />
              </Link>
              {PATIENT_LINKS.map((l) => (
                <Link key={l.path} to={l.path} className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full transition-colors ${activeStyle(l.path)}`}>
                  <l.icon size={16} weight="duotone" /> {l.label}
                </Link>
              ))}
              <NotifBell />
              {/* Profile dropdown */}
              <div className="relative ml-1" ref={profileRef}>
                <button
                  type="button"
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-1.5 rounded-full p-1 hover:bg-muted transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                    {user?.photo_url ? (
                      <img src={user.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User size={18} weight="duotone" className="text-primary" />
                    )}
                  </div>
                  <CaretDown size={12} weight="bold" className="text-muted-foreground" />
                </button>
                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 rounded-2xl border border-border bg-card shadow-lg py-2 z-50">
                    <div className="px-4 py-2 border-b border-border">
                      <div className="text-sm font-semibold text-foreground truncate">{user?.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                    </div>
                    <Link to="/profile" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors">
                      <User size={16} weight="duotone" /> Edit Profile
                    </Link>
                    <Link to="/support" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors">
                      <Ticket size={16} weight="duotone" /> Support Tickets
                    </Link>
                    <button type="button" onClick={() => setConfirmOpen(true)} className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors">
                      <SignOut size={16} weight="duotone" /> Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : !user ? (
            <>
              {showSearch && (
                <form onSubmit={handleSearch} className="relative w-56 lg:w-64">
                  <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search doctors..." className="h-10 pl-9 pr-4 rounded-full text-sm" />
                </form>
              )}
              <Link to="/apply" data-testid="nav-apply-link">
                <Button variant="ghost" className="rounded-xl text-sm">For doctors</Button>
              </Link>
              <Button type="button" variant="outline" className="rounded-xl text-sm h-10 px-4" onClick={() => openAuth("login")} data-testid="nav-login-link">
                Sign in
              </Button>
              <Button type="button" variant="outline" className="rounded-xl border-primary/30 text-primary bg-card hover:bg-primary hover:text-primary-foreground hover:border-primary text-sm h-10 px-4" onClick={() => openAuth("register")} data-testid="nav-register-link">
                Patient sign-up
              </Button>
            </>
          ) : isDoctor ? (
            <>
              {DOCTOR_LINKS.map((l) => (
                <Link key={l.path} to={l.path} className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full transition-colors ${activeStyle(l.path)}`}>
                  <l.icon size={16} weight="duotone" /> {l.label}
                </Link>
              ))}
              <Link to="/doctor/procedure-bookings" className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full transition-colors relative ${activeStyle("/doctor/procedure-bookings")}`}>
                <Syringe size={16} weight="duotone" /> Procedure Bookings
                {procBadge > 0 && (
                  <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">
                    {procBadge > 99 ? "99+" : procBadge}
                  </span>
                )}
              </Link>
              {/* Appointments dropdown */}
              <div className="relative" onMouseEnter={() => setAppointmentsOpen(true)} onMouseLeave={() => setAppointmentsOpen(false)}>
                <button
                  type="button"
                  onClick={() => setAppointmentsOpen(!appointmentsOpen)}
                  className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full transition-colors ${location.pathname.startsWith("/doctor/appointments") ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                >
                  <CalendarBlank size={16} weight="duotone" /> Appointments <CaretDown size={10} weight="bold" />
                </button>
                {appointmentsOpen && (
                  <div className="absolute left-0 top-full mt-1 w-52 rounded-2xl border border-border bg-card shadow-lg py-2 z-50" onMouseEnter={() => setAppointmentsOpen(true)} onMouseLeave={() => setAppointmentsOpen(false)}>
                    {APPOINTMENT_SUBLINKS.map((s) => (
                      <Link key={s.path} to={s.path} onClick={() => setAppointmentsOpen(false)} className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${location.pathname + location.search === s.path ? "text-primary font-semibold" : "text-foreground hover:bg-muted"}`}>
                        {s.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <NotifBell />
              {/* Profile dropdown */}
              <div className="relative ml-1" ref={profileRef}>
                <button
                  type="button"
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-1.5 rounded-full p-1 hover:bg-muted transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                    {user?.photo_url ? (
                      <img src={user.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User size={18} weight="duotone" className="text-primary" />
                    )}
                  </div>
                  <CaretDown size={12} weight="bold" className="text-muted-foreground" />
                </button>
                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 rounded-2xl border border-border bg-card shadow-lg py-2 z-50">
                    <div className="px-4 py-2 border-b border-border">
                      <div className="text-sm font-semibold text-foreground truncate">{user?.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                    </div>
                    <Link to="/doctor/manage-procedures" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors">
                      <Syringe size={16} weight="duotone" /> Manage Procedures
                    </Link>
                    <Link to="/plan-details" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors">
                      <CurrencyCircleDollar size={16} weight="duotone" /> Plan Details
                    </Link>
                    <Link to="/support" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors">
                      <Ticket size={16} weight="duotone" /> Support Tickets
                    </Link>
                    <div className="border-t border-border mx-2 my-1" />
                    <Link to="/doctor/account-settings" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors">
                      <User size={16} weight="duotone" /> Edit Profile
                    </Link>
                    <button type="button" onClick={() => setConfirmOpen(true)} className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors">
                      <SignOut size={16} weight="duotone" /> Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : isAdmin ? (
            <>
              <span className="text-sm text-muted-foreground" data-testid="nav-user-name">Hi, {user.name?.split(" ")[0]}</span>
              <Link to="/admin" data-testid="nav-dashboard-link">
                <Button variant="outline" className="rounded-full text-sm h-10 px-4">Admin</Button>
              </Link>
              <Button variant="ghost" className="rounded-full" onClick={() => setConfirmOpen(true)} data-testid="logout-btn">
                <SignOut size={18} weight="duotone" /> <span className="ml-1.5 hidden sm:inline">Sign out</span>
              </Button>
            </>
          ) : null}
        </nav>

        {/* Mobile: profile pic on right */}
        {(isPatient || isDoctor) && (
          <div className="sm:hidden flex items-center gap-2">
            <Link to="/notifications" className="relative p-1 rounded-full text-muted-foreground hover:text-foreground transition-colors">
              <Bell size={20} weight="duotone" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[10px] font-bold min-w-[16px] h-[16px] flex items-center justify-center rounded-full px-1">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Link>
            <button type="button" onClick={() => setMobileOpen(!mobileOpen)} className="rounded-full p-0.5 hover:bg-muted transition-colors">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                {user?.photo_url ? (
                  <img src={user.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User size={16} weight="duotone" className="text-primary" />
                )}
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Mobile drawer for patient */}
      {isPatient && mobileOpen && (
        <div className="sm:hidden border-t border-border bg-card px-4 py-4 space-y-1">
          {PATIENT_LINKS.map((l) => {
            const Icon = l.icon;
            return (
              <Link key={l.path} to={l.path} className={mobileLinkClass(l.path)} onClick={() => setMobileOpen(false)}>
                <Icon size={18} weight="duotone" /> {l.label}
              </Link>
            );
          })}
          <Link to="/notifications" onClick={() => setMobileOpen(false)} className={mobileLinkClass("/notifications")}>
            <Bell size={18} weight="duotone" /> Notifications
            {unreadCount > 0 && <span className="ml-auto bg-destructive text-destructive-foreground text-xs font-bold px-2 py-0.5 rounded-full">{unreadCount > 99 ? "99+" : unreadCount}</span>}
          </Link>
          <Link to="/support" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted rounded-xl transition-colors">
            <Ticket size={18} weight="duotone" /> Support Tickets
          </Link>
          <hr className="my-2 border-border" />
          <Link to="/profile" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted rounded-xl transition-colors">
            <User size={18} weight="duotone" /> Edit Profile
          </Link>
          <button type="button" onClick={() => setConfirmOpen(true)} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 rounded-xl transition-colors">
            <SignOut size={18} weight="duotone" /> Logout
          </button>
        </div>
      )}

      {/* Mobile drawer for logged-out users */}
      {!user && mobileOpen && (
        <div className="sm:hidden border-t border-border bg-card px-4 py-4 space-y-1">
          <button type="button" onClick={() => openAuth("login")} className="flex w-full items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:bg-muted transition-colors">
            Sign in
          </button>
          <button type="button" onClick={() => openAuth("register")} className="flex w-full items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:bg-muted transition-colors">
            Sign up
          </button>
          <Link to="/apply" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:bg-muted transition-colors">
            For doctors
          </Link>
        </div>
      )}

      {/* Mobile drawer for doctor */}
      {isDoctor && mobileOpen && (
        <div className="sm:hidden border-t border-border bg-card px-4 py-4 space-y-1">
          {DOCTOR_LINKS.map((l) => {
            const Icon = l.icon;
            return (
              <Link key={l.path} to={l.path} className={mobileLinkClass(l.path)} onClick={() => setMobileOpen(false)}>
                <Icon size={18} weight="duotone" /> {l.label}
              </Link>
            );
          })}
          <Link to="/doctor/procedure-bookings" onClick={() => setMobileOpen(false)} className={mobileLinkClass("/doctor/procedure-bookings")}>
            <Syringe size={18} weight="duotone" /> Procedure Bookings
            {procBadge > 0 && <span className="ml-auto bg-destructive text-destructive-foreground text-xs font-bold px-2 py-0.5 rounded-full">{procBadge > 99 ? "99+" : procBadge}</span>}
          </Link>
          <Link to="/doctor/appointments" onClick={() => setMobileOpen(false)} className={mobileLinkClass("/doctor/appointments")}>
            <CalendarBlank size={18} weight="duotone" /> All appointments
          </Link>
          <Link to="/doctor/appointments?filter=future" onClick={() => setMobileOpen(false)} className={mobileLinkClass("/doctor/appointments?filter=future")}>
            <CalendarBlank size={18} weight="duotone" /> Future appointments
          </Link>
          <Link to="/notifications" onClick={() => setMobileOpen(false)} className={mobileLinkClass("/notifications")}>
            <Bell size={18} weight="duotone" /> Notifications
            {unreadCount > 0 && <span className="ml-auto bg-destructive text-destructive-foreground text-xs font-bold px-2 py-0.5 rounded-full">{unreadCount > 99 ? "99+" : unreadCount}</span>}
          </Link>
          <hr className="my-2 border-border" />
          <Link to="/doctor/manage-procedures" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted rounded-xl transition-colors">
            <Syringe size={18} weight="duotone" /> Manage Procedures
          </Link>
          <Link to="/plan-details" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted rounded-xl transition-colors">
            <CurrencyCircleDollar size={18} weight="duotone" /> Plan Details
          </Link>
          <Link to="/doctor/account-settings" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted rounded-xl transition-colors">
            <User size={18} weight="duotone" /> Edit Profile
          </Link>
          <button type="button" onClick={() => setConfirmOpen(true)} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 rounded-xl transition-colors">
            <SignOut size={18} weight="duotone" /> Logout
          </button>
        </div>
      )}
      {isAdmin && (
        <div className="sm:hidden border-t border-border bg-card px-4 py-3 flex items-center gap-2">
          <span className="text-sm text-muted-foreground flex-1">Hi, {user?.name?.split(" ")[0]}</span>
          <Link to="/admin">
            <Button variant="outline" className="rounded-full text-sm h-10 px-4">Admin</Button>
          </Link>
          <Button variant="ghost" className="rounded-full" onClick={() => setConfirmOpen(true)}>
            <SignOut size={18} weight="duotone" />
          </Button>
        </div>
      )}
    </header>

      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} initialMode={authMode} />

      {/* Logout confirmation dialog */}
      {confirmOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-xs rounded-2xl bg-card border border-border p-6 shadow-xl">
            <p className="text-center text-sm font-semibold text-foreground">Are you sure you want to log out?</p>
            <div className="mt-5 flex items-center gap-3">
              <button type="button" onClick={() => setConfirmOpen(false)} className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                No
              </button>
              <button type="button" onClick={onLogout} className="flex-1 rounded-full bg-destructive text-destructive-foreground py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
                Yes, log out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
